import { useQueryClient, useMutation, UseMutationOptions } from 'react-query';
import PhoneOverrideApi from '@frontend/api-overrides-v2';
import { invalidateListOverrideQueries, invalidatePhoneTreeQueries } from '../../query-keys';
import { OverrideCreatePayload, OverrideUpdatePayload } from './types';

type CreateResponse = Awaited<ReturnType<typeof PhoneOverrideApi.Create>>;
type UpdateResponse = Awaited<ReturnType<typeof PhoneOverrideApi.Update>>;
type DeleteResponse = Awaited<ReturnType<typeof PhoneOverrideApi.Delete>>;

interface UseOverrideMutationsOptions {
  create?: Omit<UseMutationOptions<CreateResponse, unknown, OverrideCreatePayload>, 'mutationFn'>;
  update?: Omit<UseMutationOptions<UpdateResponse, unknown, OverrideUpdatePayload>, 'mutationFn'>;
  remove?: Omit<UseMutationOptions<DeleteResponse, unknown, { phoneOverrideId: string }>, 'mutationFn'>;
}

export const useOverrideMutations = (phoneTenantId: string, options?: UseOverrideMutationsOptions) => {
  const queryClient = useQueryClient();

  const create = useMutation({
    ...options?.create,
    mutationFn: (payload: OverrideCreatePayload) => {
      return PhoneOverrideApi.Create(payload);
    },
    onSettled: (...args) => {
      options?.create?.onSettled?.(...args);
      invalidateListOverrideQueries(queryClient, phoneTenantId);
      // Invalidate phone tree queries since they also include override data.
      invalidatePhoneTreeQueries(queryClient);
    },
  });

  const update = useMutation({
    ...options?.update,
    mutationFn: (payload: OverrideUpdatePayload) => {
      return PhoneOverrideApi.Update(payload);
    },
    onSettled: (...args) => {
      options?.update?.onSettled?.(...args);
      invalidateListOverrideQueries(queryClient, phoneTenantId);
      // Invalidate phone tree queries since they also include override data.
      invalidatePhoneTreeQueries(queryClient);
    },
  });

  const remove = useMutation({
    ...options?.remove,
    mutationFn: (payload: { phoneOverrideId: string }) => {
      return PhoneOverrideApi.Delete(payload);
    },
    onSettled: (...args) => {
      options?.remove?.onSettled?.(...args);
      invalidateListOverrideQueries(queryClient, phoneTenantId);
      // Invalidate phone tree queries since they also include override data.
      invalidatePhoneTreeQueries(queryClient);
    },
  });

  return {
    create,
    update,
    remove,
  };
};
